import { Button, Col, Drawer, Menu, Row, Typography } from "@payping/web-components";
import React, { useMemo } from "react";

import { Link, Outlet, useMatches, useNavigate } from "react-router-dom";
import { Close, LogoPersian } from "@payping/icons";
import { DashboardRoutes } from "@/router/constant";
import { useAppTranslation } from "@/utils";
import {
  bankInfoCardId,
  businessInfoCardId,
  customizeTerminalCardId,
  personalInfoCardId,
} from "@/assets/googleTrackIds";
import { MenuProps } from "@payping/web-components/components/menu";
import { useAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { coreAtom } from "@/core/state/root";

const firstUrlToSettingsAndProfileAtom = selectAtom(coreAtom, (val) => val.firstUrlToSettingsAndProfile);

const SettingsList: React.FC = () => {
  const navigate = useNavigate();
  const [firstUrlToSettingsAndProfile] = useAtom(firstUrlToSettingsAndProfileAtom);

  const { t } = useAppTranslation();

  const matches = useMatches();
  const last = useMemo(() => matches.pop(), [matches]);

  const menuItems: MenuProps["items"] = [
    {
      key: "group1",
      type: "group",
      label: t("settings.menuItemGroups.paymentGateway"),
      children: [
        {
          key: DashboardRoutes.settings.personal.root.url,
          label: (
            <Link to={DashboardRoutes.settings.personal.root.url} data-google-track={personalInfoCardId}>
              {t("settings.menuItems.personal")}
            </Link>
          ),
        },
        {
          key: DashboardRoutes.settings.business.root.url,
          label: (
            <Link to={DashboardRoutes.settings.business.root.url} data-google-track={businessInfoCardId}>
              {t("settings.menuItems.business")}
            </Link>
          ),
        },
        {
          key: DashboardRoutes.settings.bank.root.url,
          icon: null,
          label: (
            <Link to={DashboardRoutes.settings.bank.root.url} data-google-track={bankInfoCardId}>
              {t("settings.menuItems.bank")}
            </Link>
          ),
        },
      ],
    },
    {
      key: "group2",
      type: "group",
      label: t("settings.menuItemGroups.commonSettings"),
      children: [
        {
          key: DashboardRoutes.user.upgrade.url,
          label: <Link to={DashboardRoutes.user.upgrade.url}>{t("settings.menuItems.upgragePanel")}</Link>,
        },
      ],
    },
    {
      key: DashboardRoutes.settings.terminal.url,
      label: (
        <Link to={DashboardRoutes.settings.terminal.url} data-google-track={customizeTerminalCardId}>
          {t("settings.menuItems.terminals")}
        </Link>
      ),
    },
    {
      key: DashboardRoutes.settings.shop.url,
      label: (
        <Link to={DashboardRoutes.settings.shop.url}>
          {t("settings.menuItems.shop")}
        </Link>
      ),
    },
  ];

  return (
    <Drawer
      title={<LogoPersian width={60} />}
      open={true}
      height={"100%"}
      placement={"bottom"}
      closeIcon={false}
      styles={{
        body: {
          padding: "32px",
        },
        header: { paddingLeft: "32px", paddingRight: "32px", paddingTop: "18px" },
      }}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(firstUrlToSettingsAndProfile);
          }}
        />
      }
    >
      <Row className="tw-h-full">
        <Col span={6} className="tw-pt-4">
          <Typography.Title className="tw-mb-8 tw-font-medium" level={4}>
            {t("settings.title")}
          </Typography.Title>
          <Menu
            items={menuItems}
            inlineIndent={32}
            selectedKeys={last?.pathname ? [last.pathname] : undefined}
            className="font-em-11 tw-h-full"
          />
        </Col>
        <Col span={18} className="tw-pr-16 tw-pt-4">
          <Outlet />
        </Col>
      </Row>
    </Drawer>
  );
};

export default SettingsList;
